import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, CircularProgress, Alert } from "@mui/material";
import { useDataPlorApi } from "../../../hooks/useDataplorApi";

interface CancelJobDialogProps {
  jobId?: number;
  jobIds?: number[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CancelJobDialog: React.FC<CancelJobDialogProps> = ({ jobId, jobIds, open, onClose, onSuccess }) => {
  const { postDataplorApi } = useDataPlorApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [results, setResults] = useState<{ success: number[]; failed: number[]; errors: string[] }>({ success: [], failed: [], errors: [] });

  // Determine which jobs to cancel
  const jobsToCancel = jobIds || (jobId ? [jobId] : []);
  
  // Check if the number of jobs exceeds the limit
  const MAX_JOBS_LIMIT = 30;
  const exceedsLimit = jobsToCancel.length > MAX_JOBS_LIMIT;

  const handleCancel = async () => {
    // Hard stop if too many jobs are selected
    if (exceedsLimit) {
      setError(`Cannot cancel more than ${MAX_JOBS_LIMIT} jobs at once. Please select ${MAX_JOBS_LIMIT} or fewer jobs.`);
      return;
    }

    setLoading(true);
    setError(null);
    setResults({ success: [], failed: [], errors: [] });

    try {
      if (jobsToCancel.length === 1) {
        // Single job cancellation
        await postDataplorApi(`/v3/admin/export_jobs/${jobsToCancel[0]}/cancel`, {});
        setSuccess(true);
        setResults({ success: jobsToCancel, failed: [], errors: [] });
      } else {
        // Multiple job cancellation
        const results = { success: [] as number[], failed: [] as number[], errors: [] as string[] };
        
        for (const jobId of jobsToCancel) {
          try {
            await postDataplorApi(`/v3/admin/export_jobs/${jobId}/cancel`, {});
            results.success.push(jobId);
          } catch (err: any) {
            results.failed.push(jobId);
            results.errors.push(`Job ${jobId}: ${err?.message || "Failed to cancel"}`);
          }
        }

        setResults(results);
        setSuccess(results.success.length > 0);
        
        if (results.failed.length === 0) {
          setError(null);
        } else if (results.success.length === 0) {
          setError("All jobs failed to cancel");
        } else {
          setError(`Some jobs failed to cancel (${results.failed.length}/${jobsToCancel.length})`);
        }
      }

      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError(err?.message || "Failed to cancel job(s).");
    } finally {
      setLoading(false);
    }
  };

  const getDialogTitle = () => {
    if (jobsToCancel.length === 1) {
      return "Cancel Analysis Job";
    }
    return `Cancel ${jobsToCancel.length} Analysis Jobs`;
  };

  const getDialogContent = () => {
    if (exceedsLimit) {
      return `You selected ${jobsToCancel.length} jobs, but you can only cancel up to ${MAX_JOBS_LIMIT} at a time.\n\nTo select a range: Click one job, then hold Shift and click another.\n\nNote: "Select All" will select every job in the list, which may exceed the ${MAX_JOBS_LIMIT} job limit.`;
    }
    
    if (jobsToCancel.length === 1) {
      return `Are you sure you want to cancel this job: ${jobsToCancel[0]}?`;
    }
    return `Are you sure you want to cancel ${jobsToCancel.length} jobs?`;
  };

  const getButtonText = () => {
    if (jobsToCancel.length === 1) {
      return "Cancel Job";
    }
    return `Cancel ${jobsToCancel.length} Jobs`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        <Typography sx={{ whiteSpace: 'pre-line' }}>{getDialogContent()}</Typography>
        
        {loading && (
          <Box mt={2} display="flex" alignItems="center" gap={1}>
            <CircularProgress size={24} />
            <Typography>Cancelling job{jobsToCancel.length !== 1 ? 's' : ''}...</Typography>
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
            {results.errors.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2" component="div">
                  {results.errors.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </Typography>
              </Box>
            )}
          </Alert>
        )}
        
        {success && results.success.length > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {results.success.length === 1 
              ? "Job cancelled successfully."
              : `${results.success.length} job${results.success.length !== 1 ? 's' : ''} cancelled successfully.`
            }
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Close</Button>
        <Button 
          onClick={handleCancel} 
          color="error" 
          disabled={loading || success || exceedsLimit}
        >
          {getButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelJobDialog;
